import styles from 'components/cases/common/Interview/Interview.module.scss'
import TitleFlex from 'components/cases/common/TitleFlex'
import { useEffect, useRef } from 'react'
import { gsap, Power4 } from 'gsap'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'
import InterviewContents from 'components/cases/common/Interview/InterviewContents'
import { ContentData } from 'components/cases/common/Interview/fragments/type'
gsap.registerPlugin(ScrollTrigger)

const sectionTitle: { title: string; sub_title: string } = {
  title: 'Interview',
  sub_title: 'インタビュー',
}

const contentData: ContentData[][] = [
  [
    {
      type: 'sectionTitle',
      text: 'より早い価値提供を目指し、ウォーターフォールからアジャイルへ',
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/dir/interview01.webp',
      alt: '左から株式会社大和総研の松井さん、林さん、近藤さん、泉さん',
      text: '左から株式会社大和総研の松井さん、林さん、近藤さん、泉さん',
    },
    {
      type: 'textBox',
      texts: ['―― 生成AIを活用したチャットサービス導入の背景と、期待された役割についてお聞かせください。'],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      name: '林さん',
      texts: [
        <>
          <p>
            以前提供していたルールベースのチャットでは、決まった質問にしか回答できず、お客さまが知りたい情報にたどり着くにはルールに沿った質問の仕方が必要でした。そこで、AIの力を借りて、新しいチャットサービスを提供したいと考えました。Webページ上のFAQやマニュアルといった情報を、このチャットの裏側で
            <span className={styles.textbold}>
              RAG（Retrieval-Augmented Generation）という仕組みを使ってデータベース化
            </span>
            し、AIがそこから回答を探してきて、お客さまの質問の意図に合った回答を返すという、
            <span className={styles.textbold}>お客さまに対する体験の向上</span>を期待して導入しました。
          </p>
        </>,
      ],
    },
    {
      type: 'textBox',
      texts: ['―― 今回のプロジェクトでアジャイル開発を採用された背景をお聞かせください。'],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      name: '林さん',
      texts: [
        <>
          <p>
            当社は金融向けのサービス提供を主としているため、多くのシステム開発が、要件定義から始まるウォーターフォール開発で行われてきました。ただ、堅牢性が最優先される領域ではこの手法が有効ですが、今回のチャットサービスは
            <span className={styles.textbold}>「お客さまにより早く価値を届ける」</span>
            という目的が最優先でした。仮にサービスが一時的に停止しても、お取引には直接影響しない重要度も踏まえ、まずはサービスを世に出し、実際のユーザー様の声を聞きながら
            <span className={styles.textbold}>継続的に改善していく</span>アプローチが最適だと判断しました。
          </p>
        </>,
      ],
    },
    {
      type: 'greyBox',
      name: '泉さん',
      texts: [
        <>
          <p>
            正直に申し上げると、当社はアジャイル開発の経験が豊富ではありませんでした。書籍や研修で学んではいましたが、実践となると話は別です。それならば、アジャイルの
            <span className={styles.textbold}>専門的な知見を持つプロフェッショナル</span>
            と協働することで、実践的なノウハウを吸収しながらプロジェクトを成功に導くのがベストだと考えました。
          </p>
        </>,
      ],
    },
    {
      type: 'textBox',
      texts: ['―― ベンダー選定において、どのような点を重視されましたか？'],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      name: '林さん',
      texts: [
        <>
          <p>
            第一に重視したポイントは、<span className={styles.textbold}>アジャイル開発の実践経験</span>
            です。理論だけでなく、実際にスピード感を持って開発し、継続的な改善サイクルを回せることが必須でした。
          </p>
          <p>
            <span className={styles.space_top}>
              第二に、<span className={styles.textbold}>UX/UIデザインの専門性</span>
              です。これまでWeb画面系の作成では、当社はほとんどデザインを担ってこなかったため、我々だけで作るとお堅い画面になりがちでした。しかし今回は、お客さまにとって使いやすく、親しみやすいインターフェースを実現したいという思いがありました。当然、バックエンドの開発まで含めてトータルでお任せできる技術力も求めていました。
            </span>
          </p>
        </>,
      ],
    },
    {
      type: 'greyBox',
      name: '近藤さん',
      texts: [
        <>
          <p>
            最終的な決め手は、プロジェクトに対する<span className={styles.textbold}>真摯な熱意と、迅速な対応力</span>
            にあったと感じています。複数のベンダーと商談を進める中で、アイスリーデザインさんは「理解度を上げたいので打ち合わせをさせてください」と提案してくださり、要件の解像度を徹底的に高めてから最適な体制をご提案いただきました。打ち合わせが終わると「翌営業日にはご提案します」というレベル感で動いていただき、そうしたスピード感にも信頼がおけ、最終的に貴社を「選びたい」と思わせていただいたと感じています。
          </p>
        </>,
      ],
    },
  ],
  [
    {
      type: 'sectionTitle',
      text: 'わずか1か月でのプロトタイプ作成を支えた開発プロセス',
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/dir/interview02.webp',
      alt: '左からアイスリーデザインの平、高篠',
      text: '左からアイスリーデザインの平、高篠',
    },
    {
      type: 'textBox',
      texts: [
        '―― アジャイル開発のスピード感を支えるため、開発チーム内で実践された工夫や技術的な取り組みがあれば教えてください。',
      ],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      name: '平',
      texts: [
        <>
          <p>
            AI駆動開発の取り組みを積極的に進めました。1週間のスプリントを回す中で、AIを以下のように活用し、開発サイクル全体を高速化しています。
          </p>
          <p>
            <span className={styles.space_top}>
              まず、<span className={styles.textbold}>開発プロセスでは「Cursor」の活用</span>を進め、「仕様 → 詳細設計 →
              タスク分解」といった設計フェーズをAIに支援させることで、仕様駆動開発を推進しました。AIが実装しやすい状態を整備することで、設計から実装、レビューまでのサイクル全体を高速化しています。
            </span>
          </p>
          <p>
            <span className={styles.space_top}>
              次に、<span className={styles.textbold}>コードレビューの高速化</span>
              にも取り組みました。開発のマージ段階でAIレビューを組み込み、指摘の自動化やチェック項目の標準化を実施。これによりレビュー待ち時間が短縮され、品質のばらつきも抑えられました。
            </span>
          </p>
          <p>
            <span className={styles.space_top}>
              さらに、<span className={styles.textbold}>会議の効率化と情報整理</span>
              にもAIを活用しています。オンライン会議の内容を自動で文字起こしし、AIが要点抽出や決定事項の整理を行うワークフローを構築。会議後すぐにサマリーが共有されることで議事録作成の手間が軽減され、クライアントとの打ち合わせでも「不明点」や「追加確認すべき仕様」を自動抽出する仕組みにより、仕様漏れを早い段階から防げるようになりました。
            </span>
          </p>
        </>,
      ],
    },
    {
      type: 'textBox',
      texts: [
        '―― 今回のプロジェクトでは、デザイナーがスクラムチームの一員として参画しました。この体制が仕様検討や設計のスピード・質にどういった影響を与えたかお聞かせください。',
      ],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      name: '林さん',
      texts: [
        'デザイン面の知見がない我々に対して、デザイナーの方がデザインの設計を言語化し、論理的に画面イメージや色について説明してくださったので、非常に分かりやすく、スムーズに受け入れられました。',
      ],
    },
    {
      type: 'greyBox',
      name: '高篠',
      texts: [
        <>
          <p>林様がおっしゃる通り、デザインの「意図」を言語化することを意識しています。</p>
          <p>
            <span className={styles.space_top}>
              画面デザインでは、ユーザー導線や機能の優先度に基づき、なぜそのレイアウトが最適かを論理的にご説明しました。たとえば、ユーザーとAIロボットの吹き出しには最大幅を意図的に設けています。これは、一般的に快適に読める文字数は1行あたり35〜45文字程度とされており（※）、可読性の確保はユーザー体験の向上に不可欠だと考えたからです。UI上ではこの字数になるような横幅を設定することで、長文の可読性を担保しました。
              <br />
              ※言語やフォントサイズ、表示デバイスなどによって最適値が異なります。本プロジェクトでは、日本語のWeb環境における一般的な可読性を考慮して設定しています。
            </span>
          </p>
          <p>
            <span className={styles.space_top}>
              また、色設計では、コーポレートカラーの赤色が、アクセシビリティやUI上のシステムカラー（警告色など）と競合する課題を明確化しました。その懸念点を解消するため、色の調整が必要であることをお伝えし、アプリケーション全体のトーン＆マナーの検討を論理的に進めました。
            </span>
          </p>
        </>,
      ],
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/dir/interview03.webp',
      alt: 'チャットUIでは1行あたり38文字に設定',
    },
    {
      type: 'greyBox',
      name: '近藤さん',
      texts: [
        'デザイナーが考えたキャラクターなどが入ったデザインモックを見せることで視覚的なイメージが伝わりやすくなりました。これにより、エンドクライアント側も「こういうデザインにしたい」といった意見を出しやすく、それを柔軟に毎週のスプリントに取り込んでいただいたため、デザインの決定が柔軟に、かつ迅速に進められました。',
      ],
    },
  ],
  [
    {
      type: 'sectionTitle',
      text: '課題解決率の大幅アップを実現。変化の時代に応える開発とは',
    },
    {
      type: 'textBox',
      texts: ['―― リリース後、社内外からどのような反応・反響がありましたか。'],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      name: '林さん',
      texts: [
        <>
          <p>
            「お客さまからの質問に対して沿った回答を返せているか」を測る
            <span className={styles.textbold}>課題解決率</span>
            が、生成AIチャットの導入後大幅アップしており、ルールベースだった以前と比べて、お客さまの課題解決に大きく貢献できていると考えています。
          </p>
          <p>
            <span className={styles.space_top}>
              また、リリース直前にエンドクライアントが別サービスにおいて
              <span className={styles.textbold}>2段階認証</span>
              を導入したタイミングと重なり、コンタクトセンターへの電話やメールでの問い合わせが殺到し、対応がひっ迫していました。このチャットが2段階認証に関する質問にも回答できるようになったことで、
              <span className={styles.textbold}>コンタクトセンターの負荷軽減に貢献</span>
              し、トップの方からも感謝の言葉をいただきました。
            </span>
          </p>
        </>,
      ],
    },
    {
      type: 'greyBox',
      name: '泉さん',
      texts: [
        'ちょうどフィッシングによる不正ログイン事件が話題になり、各社がセキュリティ対策を進める中で2段階認証が始まったため、問い合わせが集中しました。このAIチャットがそれを一部吸収してくれたことで、結果的に良いタイミングでのリリースとなったと感じています。',
      ],
    },
    {
      type: 'textBox',
      texts: ['―― アジャイル開発を経験して、社内メンバーの意識変化や学びなどがあれば教えてください。'],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      name: '松井さん',
      texts: [
        <>
          <p>
            我々のように金融の厳格なシステム開発をやってきた会社では、新しい手法は難しいのではないかという感覚がありましたが、実際にやってみて
            <span className={styles.textbold}>成功した</span>
            という実感を得られました。これまでの開発手法では、設計の変更に時間がかかり仕様変更に弱い動きになっていましたが、アジャイルではそのプロセスを簡略化することで、関連する機能の調整や改善を素早く繰り返せるようになり、非常に強力な手法だと感じました。
          </p>
        </>,
      ],
    },
    {
      type: 'greyBox',
      name: '林さん',
      texts: [
        <>
          <p>
            特に、プロジェクト開始から<span className={styles.textbold}>1か月足らずの期間</span>
            で、テスト環境で動作するものが出来上がり、ユーザーに見せ始められたという
            <span className={styles.textbold}>スピード感</span>
            は、ウォーターフォールでは実現が難しい点で、一番良かったと感じています。
          </p>
        </>,
      ],
    },
    {
      type: 'textBox',
      texts: ['―― AI導入やアジャイル開発に踏み切れない企業に対して、メッセージやアドバイスをお願いします。'],
      wide: false,
      bold: true,
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/dir/interview04.webp',
      alt: '林さん',
    },
    {
      type: 'greyBox',
      name: '林さん',
      texts: [
        <>
          <p>
            今は<span className={styles.textbold}>環境変化が激しく、スピード感が求められる時代</span>
            です。新しいサービスをいかに早くリリースするかという点は、どの企業にとっても重要なポイントです。アイスリーデザインさんのようにアジャイル開発にたけていて、サービスを迅速にリリースできるパートナーと協働し、柔軟なもの作りに取り組める体制を構築することは、他社さんにも強くお勧めできるポイントです。
          </p>
        </>,
      ],
    },
  ],
]

const tweenVars: gsap.TimelineVars = {
  y: 50,
  duration: 1,
  opacity: 0,
  ease: Power4.easeOut,
}

const Interview: React.FC = () => {
  const timeline = gsap.timeline()
  const wrapper = useRef<HTMLElement>()
  const image = useRef<HTMLImageElement>()
  const text_container = useRef<HTMLDivElement>()

  useEffect(() => {
    timeline.from(image.current, tweenVars, 0.2)
    timeline.from(text_container.current, tweenVars, 0.4)

    ScrollTrigger.create({
      animation: timeline,
      trigger: wrapper.current,
      start: 'top-=100 90%',
      toggleActions: 'play none none play',
    })
  }, [])

  return (
    <section className={styles.wrapper} ref={wrapper}>
      <div className={styles.content}>
        <TitleFlex sectionTitle={sectionTitle} />
        <div className={styles.article_container} ref={text_container}>
          <InterviewContents contentData={contentData} />
        </div>
      </div>
    </section>
  )
}

export default Interview
